import React, { useEffect, useState, useCallback } from "react";
import { useParams, useSearchParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { format } from "date-fns";
import { sv } from "date-fns/locale";
import { Download, Share2 } from "lucide-react";
import { api, API } from "@/lib/handball";
import AppHeader from "@/components/AppHeader";
import StatsView from "@/components/StatsView";
import ShareDialog from "@/components/ShareDialog";
import StatsFilters from "@/components/StatsFilters";
import TournamentFilterBar, { GoalkeeperComparisonCard, TournamentMatchList } from "@/components/TournamentFilterBar";
import { Button } from "@/components/ui/button";

/**
 * mode:
 *  - "session": stats for a specific session (uses :sessId from params)
 *  - "period":  stats for a date range (uses ?from=&to= from query)
 *  - "all":     all-time stats for the goalkeeper
 */
export default function EvaluateView({ mode }) {
  const { gkId, sessId, tournamentId } = useParams();
  const [params] = useSearchParams();
  const navigate = useNavigate();

  const dateFrom = params.get("from");
  const dateTo = params.get("to");

  const [goalkeeper, setGoalkeeper] = useState(null);
  const [session, setSession] = useState(null);
  const [tournament, setTournament] = useState(null);
  const [tournamentMatches, setTournamentMatches] = useState([]);
  const [tournamentGoalkeepers, setTournamentGoalkeepers] = useState([]);
  const [tournamentFilter, setTournamentFilter] = useState({ goalkeeperId: null, sessionId: null });
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [shareOpen, setShareOpen] = useState(false);
  const [filters, setFilters] = useState({ zones: [], distances: [], shooter: null, contact: null, lob: null });

  const buildStatsParams = useCallback(() => {
    const p = new URLSearchParams();
    if (mode !== "tournament") p.set("goalkeeper_id", gkId);
    if (mode === "session") p.set("session_id", sessId);
    if (mode === "tournament") {
      p.set("tournament_id", tournamentId);
      if (tournamentFilter.goalkeeperId) p.set("goalkeeper_id", tournamentFilter.goalkeeperId);
      if (tournamentFilter.sessionId) p.set("session_id", tournamentFilter.sessionId);
    }
    if (mode === "period") {
      p.set("date_from", dateFrom || "");
      p.set("date_to", dateTo || "");
    }
    filters.zones.forEach((z) => p.append("zone", z));
    filters.distances.forEach((d) => p.append("distance", d));
    // Shooter filter only makes sense for a single session
    if (mode === "session" && filters.shooter != null) {
      p.set("shooter", String(filters.shooter));
    }
    if (filters.contact != null) p.set("contact", String(filters.contact));
    if (filters.lob != null) p.set("lob", String(filters.lob));
    return p;
  }, [gkId, sessId, tournamentId, mode, dateFrom, dateTo, filters, tournamentFilter]);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        if (mode === "tournament") {
          const res = await api.get(`/tournaments/${tournamentId}/sessions`);
          setTournament(res.data.tournament);
          setTournamentMatches(res.data.sessions || []);
          setTournamentGoalkeepers(res.data.goalkeepers || []);
          // When entered via /gk/:gkId/evaluate/tournament/:tid we're clearly
          // in a specific goalkeeper's context — default the filter to that GK
          // so stats aggregate across all matches in the tournament for THAT
          // goalkeeper only. User can still switch to "Alla" or another GK
          // via TournamentFilterBar if they have access.
          if (gkId) {
            const hasGk = (res.data.goalkeepers || []).some((g) => g.id === gkId);
            if (hasGk) {
              setTournamentFilter((prev) =>
                prev.goalkeeperId == null && prev.sessionId == null
                  ? { goalkeeperId: gkId, sessionId: null }
                  : prev
              );
            }
          }
        } else {
          const gksRes = await api.get("/goalkeepers");
          const gk = gksRes.data.find((g) => g.id === gkId);
          if (!gk) {
            toast.error("Målvakt hittades ej");
            navigate("/");
            return;
          }
          setGoalkeeper(gk);
          if (mode === "session") {
            const sessRes = await api.get("/sessions", {
              params: { goalkeeper_id: gkId },
            });
            const s = sessRes.data.find((x) => x.id === sessId);
            if (!s) {
              toast.error("Session hittades ej");
              navigate(`/gk/${gkId}/evaluate`);
              return;
            }
            setSession(s);
          } else if (mode === "period") {
            if (!dateFrom || !dateTo) {
              toast.error("Datumintervall saknas");
              navigate(`/gk/${gkId}/evaluate`);
              return;
            }
          }
        }
      } finally {
        setLoading(false);
      }
    })();
  }, [gkId, sessId, tournamentId, mode, dateFrom, dateTo, navigate]);

  // Refetch stats whenever filters change
  useEffect(() => {
    (async () => {
      try {
        const statsRes = await api.get(`/stats?${buildStatsParams().toString()}`);
        setStats(statsRes.data);
      } catch (err) {
        console.debug("[evaluate] stats fetch failed", err);
      }
    })();
  }, [buildStatsParams]);

  const exportExcel = () => {
    const p = buildStatsParams();
    window.open(`${API}/export/excel?${p.toString()}`, "_blank");
  };

  const scopeLabel = () => {
    if (mode === "tournament" && tournament) {
      // When a specific match is picked inside the tournament, surface its name as subtitle
      const picked = tournamentFilter.sessionId
        ? tournamentMatches.find((m) => m.id === tournamentFilter.sessionId)
        : null;
      const gkPicked = tournamentFilter.goalkeeperId
        ? tournamentGoalkeepers.find((g) => g.id === tournamentFilter.goalkeeperId)
        : null;
      if (picked) {
        return `${picked.name}${picked.opponent ? ` · vs ${picked.opponent}` : ""} · ${tournament.name}`;
      }
      if (gkPicked) {
        return `${gkPicked.name} · ${tournament.name}`;
      }
      return `${tournament.name} · ${tournament.start_date} — ${tournament.end_date}`;
    }
    if (mode === "session" && session)
      return `${session.name} · ${session.session_type === "match" ? "Match" : "Träning"}${session.opponent ? ` · vs ${session.opponent}` : ""}`;
    if (mode === "period" && dateFrom && dateTo) {
      const f = format(new Date(dateFrom), "d MMM yyyy", { locale: sv });
      const t = format(new Date(dateTo), "d MMM yyyy", { locale: sv });
      return `Period ${f} — ${t}`;
    }
    return "All samlad data";
  };

  if (loading || (mode !== "tournament" && !goalkeeper) || (mode === "tournament" && !tournament)) {
    return (
      <div className="min-h-screen">
        <AppHeader
          subtitle="Laddar…"
          backTo={`/gk/${gkId}/evaluate`}
          backLabel="Utvärdera"
        />
      </div>
    );
  }

  return (
    <div className="min-h-screen">
      <AppHeader
        subtitle={mode === "tournament" ? `Turnering · ${tournament?.name || ""}` : `Utvärdering · ${goalkeeper?.name || ""}`}
        backTo={mode === "tournament" ? `/gk/${gkId}/evaluate` : `/gk/${gkId}/evaluate`}
        backLabel="Utvärdera"
        right={
          <>
            {mode !== "period" && (
              <Button
                data-testid="share-open-btn"
                onClick={() => setShareOpen(true)}
                variant="outline"
                className="h-11 rounded-none border-[#27272A] bg-transparent hover:bg-[#171717] text-white"
              >
                <Share2 size={16} className="mr-2" />
                Dela
              </Button>
            )}
            <Button
              data-testid="export-excel-btn"
              onClick={exportExcel}
              className="h-11 rounded-none bg-[#34C759] hover:bg-[#2fb350] text-black font-display uppercase tracking-widest"
            >
              <Download size={16} className="mr-2" />
              Excel
            </Button>
          </>
        }
      />

      <main className="max-w-[1200px] mx-auto px-6 py-8 space-y-6">
        <section className="card-tactical p-6" data-testid="eval-header">
          <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-display mb-2">
            {mode === "session"
              ? "Enskild session"
              : mode === "period"
              ? "Tidsperiod"
              : mode === "tournament"
              ? "Turnering/serie"
              : "All data"}
          </div>
          <div
            className="font-display font-black uppercase tracking-widest text-3xl md:text-4xl text-white"
            data-testid="eval-goalkeeper-name"
          >
            {mode === "tournament"
              ? (tournamentFilter.sessionId
                  ? tournamentMatches.find((m) => m.id === tournamentFilter.sessionId)?.name
                  : tournamentFilter.goalkeeperId
                  ? tournamentGoalkeepers.find((g) => g.id === tournamentFilter.goalkeeperId)?.name
                  : tournament?.name)
              : goalkeeper?.name}
          </div>
          <div
            className="mt-2 text-zinc-400 font-display uppercase tracking-widest text-sm"
            data-testid="eval-scope-label"
          >
            {scopeLabel()}
          </div>
          {mode === "session" && session?.completed && (session.final_score || session.notes) && (
            <div
              className="mt-5 pt-5 border-t border-[#27272A] space-y-4"
              data-testid="eval-match-summary"
            >
              {session.final_score && (
                <div>
                  <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-display mb-1.5">
                    Slutresultat
                  </div>
                  <div
                    data-testid="eval-final-score"
                    className="font-display font-black uppercase tracking-widest text-3xl text-[#FFD60A]"
                  >
                    {session.final_score}
                  </div>
                </div>
              )}
              {session.notes && (
                <div>
                  <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-display mb-1.5">
                    Kommentar
                  </div>
                  <div
                    data-testid="eval-match-notes"
                    className="text-zinc-200 text-sm whitespace-pre-wrap leading-relaxed"
                  >
                    {session.notes}
                  </div>
                </div>
              )}
            </div>
          )}
        </section>

        {mode === "tournament" && (
          <TournamentFilterBar
            goalkeepers={tournamentGoalkeepers}
            matches={tournamentMatches}
            value={tournamentFilter}
            onChange={setTournamentFilter}
          />
        )}

        {mode === "tournament" &&
          !tournamentFilter.goalkeeperId &&
          !tournamentFilter.sessionId && (
            <GoalkeeperComparisonCard goalkeepers={tournamentGoalkeepers} />
          )}

        {mode !== "tournament" && (
          <StatsFilters
            goalkeeperId={gkId}
            sessionId={mode === "session" ? sessId : null}
            dateFrom={dateFrom}
            dateTo={dateTo}
            value={filters}
            onChange={setFilters}
          />
        )}

        <StatsView
          stats={stats}
          testIdPrefix="eval-stats"
          showMissFrame={mode === "session" && filters.shooter != null}
        />

        {mode === "tournament" && (
          <TournamentMatchList
            matches={tournamentMatches}
            value={tournamentFilter}
            onChange={setTournamentFilter}
          />
        )}
      </main>

      <ShareDialog
        open={shareOpen}
        onOpenChange={setShareOpen}
        goalkeeperId={mode === "tournament" ? null : gkId}
        goalkeeperName={mode === "tournament" ? "" : goalkeeper?.name}
        sessionId={mode === "session" ? sessId : ""}
        sessionName={mode === "session" ? session?.name : ""}
        tournamentId={mode === "tournament" ? tournamentId : null}
        tournamentName={mode === "tournament" ? tournament?.name : ""}
      />
    </div>
  );
}
